<template>
  <section class="help-article-wrapper flex flex-col bg-white">
    <Head>
      <Title>{{ article.title }}</Title>
    </Head>

    <HelpHero
      :breadcrumbs="[
        {
          title: $messages('label.home'),
          to: $mRouter.getLocalRoute({ name: 'index' }),
        },
        {
          title: $messages('page_labels.help'),
        },
      ]"
      :headline="articleCategoryName"
      :title="$messages('help.help_hero.title')"
    />
    <HelpTheHelpSearch />
    <article class="container py-12 md:pb-20 lg:max-w-185 lg:mx-auto">
      <header>
        <h1
          class="uppercase font-druk text-display-10 tracking-02-em leading-10 md:text-display-14 md:leading-14 text-center md:text-left mb-12"
        >
          {{ article.title }}
        </h1>
      </header>
      <div
        class="article-content"
        v-html="article.body"
      />
    </article>
    <HelpArticleList
      v-if="hasRelatedArticles"
      :title="$messages('help.related_articles')"
      :articles="article.relatedArticles"
      random
      :limit="6"
    />
    <HelpTheHelpContact />
  </section>
</template>
<script setup>
import { getHelpArticleBySlug } from '@/api/help'
import { isNullOrUndefined } from '@/utils/comparators'
definePageMeta({
  layout: 'default',
  stopDefaultSwitch: true
})
const locale = useLocale()
const route = useRoute()
const params = route.params
const { $bus, $mRouter } = useNuxtApp()
const { setLocale } = useI18n()

const article = await getHelpArticleBySlug(locale, params.slug)

const articleCategoryName = computed(() => {
  return article.category?.name
})

const hasRelatedArticles = computed(() => {
  return !isNullOrUndefined(article.relatedArticles) && article.relatedArticles.length > 0
})

$bus.onDynamicRouteChangeLanguage(route.meta.key, (val) => {
  setLocale(val.destinationLocale).then(() => {
    const slugObject = article.alternativeSlugs.find(x => x.locale === val.destinationLocale)
    !isNullOrUndefined(slugObject)
      ? window.location.href = $mRouter.getLocalRoute({ name: 'help-article-slug', params: { slug: slugObject.slug } }).href
      : window.location.href = $mRouter.getLocalRoute({ name: 'help' }).href
  })
})

</script>

  <style lang="postcss" scoped>
  :deep(.article-content) {
    @apply mx-auto text-smallest;
    iframe {
      max-width: 100%;
    }
    /* img {
      width: 100%;
      display: block;
      margin: auto;
    } */

    a {
      @apply font-bold !important;
      word-wrap: break-word;
      span {
        @apply font-bold !important;
      }
    }
    a[href] {
      word-wrap: break-word;
      text-decoration: underline;
    }

    table {
      @apply mx-auto w-full;
      border-collapse: collapse;
      border-spacing: 0;

      th {
        border:2px solid #000000;
        padding: 5px;
      }

      td {
        border:1px solid #000000;
        padding: 5px;
        word-wrap: break-word;
      }

      p {
        @apply mb-0;
      }
    }

    .intercom-align-center {
      text-align: center;
    }

    .intercom-h2b-button {
      background-color: #794DFF;
      border-color: #794DFF;
      color: #ffffff;
      font-size: 1rem;
      line-height: 1.5rem;
      border-radius: 999px;
      padding: 0.375rem 1.5rem;
      border-width: 0.125rem;
      display: inline-block;
      vertical-align: middle;
      text-align: center;
      transition: transform 0.2s ease-in;
      text-decoration: none !important;

      &:focus {
        outline: none;
        text-decoration: none;
        transform: scale(0.95);
      }
  }

    ul ,ol, a, p {
      @apply text-smaller;
    }
    p {
      @apply mb-6;

      &:last-child {
        @apply mb-0;
      }
    }
    h1 {
      @apply font-gt-america font-bold text-base leading-6 mb-tiny opacity-100 normal-case;
    }
    h2 {
      @apply font-gt-america font-normal text-base leading-6 mb-tiny opacity-100 normal-case;
    }

    @screen md {
      ul ,ol, a, p {
        @apply text-display-5 leading-8;
      }
      p {
        @apply mb-8;
      }
      h1 {
        @apply mb-6 text-biggest leading-8;
      }
      h2 {
        @apply mb-6 text-biggest leading-8;
      }

      table {
        @apply w-auto;
      }
    }

    @screen lg {
      p {
        @apply mb-12;
      }
    }

    ul {
      @apply list-inside list-none mb-tiny;

      li {
        @apply relative;
        left: 1.5rem;
        max-width: calc(100% - 1.5rem);

        @screen md {
          left: 2.5rem;
          max-width: calc(100% - 2.5rem);
        }

        &::before {
          content: "\e00c";
          font-family: 'majority-dsm' !important;
          font-style: normal !important;
          font-weight: normal !important;
          font-variant: normal !important;
          text-transform: none !important;
          speak: none;
          line-height: 1;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          @apply absolute transform -translate-y-1/2 top-1/2;
          left: -1.5rem;

          @screen md {
            left: -2.5rem;
          }
        }

        &:not(:last-child) {
          margin-bottom: 0.5em;
        }
      }

      p {
        @apply mb-0 inline;
      }
    }

    ol {
      @apply list-inside list-none mb-tiny;
      counter-reset: job-counter;

      li {
        counter-increment: job-counter;

        &:not(:last-child) {
          margin-bottom: 0.5em;
        }

        &::before {
          @apply inline-block font-bold font-druk mr-2;
          content: counter(job-counter);
        }
      }

      p {
        @apply mb-0 inline;
      }
    }
    li {
      > ul, > ol {
        @apply pl-large
      }
      ul {
        @apply list-circle;
        li {
          ul {
            @apply list-square
          }
        }
      }
    }
  }
  </style>
